import { useState, useMemo, useRef } from "react";
import { useTranslate } from "@refinedev/core";

import { ApiResponse } from "@shared/types";
import { ModalRef } from "@shared/components";
import { usePaginatedTable, useQueryParams } from "@shared/hooks";
import { useFetchResourcesByIdsQuery } from "@shared/services/queries";
import { MapIntegrationPlan } from "@modules/mapIntegrationPlans/types";
import { Agreement } from "@modules/agreements/types";
import { Plan } from "@modules/plans/types";

export function useMapIntegrationPlanList() {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const createMapIntegrationPlanModalRef = useRef<ModalRef>(null);
  const editMapIntegrationPlanModalRef = useRef<ModalRef>(null);

  const translate = useTranslate();
  const { getQueryParams } = useQueryParams();

  const { rows, loading, refetch, ...tableProps } = usePaginatedTable<MapIntegrationPlan>({
    resource: "v1/subscription_mapintegrationplan_admin",
    filters: getQueryParams(),
    sorters: { ordering: "-id" },
  });

  const agreementsIds = useMemo(
    () => [...new Set(rows?.map((mapintegrationplan) => mapintegrationplan.agreement).filter(
      (id): id is number => !!id
    ))],
    [rows]
  );

  const plansIds = useMemo(
    () => [...new Set(rows?.map((mapintegrationplan) => mapintegrationplan.plan).filter(
      (id): id is number => !!id
    ))],
    [rows]
  );

  const { data: agreements } = useFetchResourcesByIdsQuery<ApiResponse<Agreement>>({
    resource: "v1/agreement_admin",
    ids: agreementsIds,
  });

  const { data: plans } = useFetchResourcesByIdsQuery<ApiResponse<Plan>>({
    resource: "v1/subscription_plan_admin",
    ids: plansIds,
  });

  const dataToExport = useMemo(() => {
    return selectedRows.length > 0
      ? rows.filter((row) => selectedRows.includes(row.id))
      : rows;
  }, [selectedRows, rows]);

  return {
    rows,
    loading,
    tableProps,
    selectedRows,
    dataToExport,

    createMapIntegrationPlanModalRef,
    editMapIntegrationPlanModalRef,
    agreements: agreements?.results,
    plans: plans?.results,
    refetch,
    setSelectedRows,
    translate,

  };
}
